import { useEffect, useRef } from 'react';

import { applyRowMasksToData, getUiDetails } from 'services/datachecks/datachecks';
import { flattenedDataToEnrichedCanonical } from 'services/datachecks/flattenedDataToEnrichedCanonical';
import { logError } from 'services/logging/logging';
import { usePackingListStore } from 'stores/packingList/packingList';
import { DocumentType, MessageV2DTO, ProcessSpecificationDTO } from 'support/types';

export const usePrecomputedEnrichedOriginal = (
  message: MessageV2DTO | undefined,
  processSpecificationFields: ProcessSpecificationDTO['fields'] | undefined,
  documentType: DocumentType | undefined,
) => {
  const enrichedOriginalRef = useRef<Promise<unknown> | null>(null);
  const packingListEnabled = usePackingListStore((state) => state.enabled);

  useEffect(() => {
    if (!message?.canonical || !processSpecificationFields || !documentType) {
      enrichedOriginalRef.current = null;
      return;
    }
    const pending = originalCanonicalThroughFormPipeline(
      processSpecificationFields,
      documentType,
      message.canonical,
      packingListEnabled,
    );
    // Swallow the rejection on this floating chain so a load-time failure never
    // surfaces as an unhandled rejection; the consumer recomputes on demand and
    // surfaces the error there. Only clear the ref if this promise is still the
    // active one — a newer canonical may have installed a healthy promise meanwhile.
    pending.catch((err) => {
      logError(err);
      if (enrichedOriginalRef.current === pending) {
        enrichedOriginalRef.current = null;
      }
    });
    enrichedOriginalRef.current = pending;
  }, [message?.canonical, processSpecificationFields, documentType, packingListEnabled]);

  return () => {
    if (enrichedOriginalRef.current) {
      return enrichedOriginalRef.current;
    }
    if (!message?.canonical || !processSpecificationFields || !documentType) {
      return Promise.reject(new Error('Cannot enrich original: missing message, specification, or document type'));
    }
    return originalCanonicalThroughFormPipeline(
      processSpecificationFields,
      documentType,
      message.canonical,
      packingListEnabled,
    );
  };
};

const originalCanonicalThroughFormPipeline = async (
  processSpecificationFields: ProcessSpecificationDTO['fields'],
  documentType: DocumentType,
  canonical: MessageV2DTO['canonical'],
  packingListEnabled: boolean,
) => {
  const { values, view } = await getUiDetails({ fields: processSpecificationFields, data: canonical });
  const flattenedOriginal = applyRowMasksToData(values, view);
  // Strip the baseline the same way the edited payload is stripped, so the no-change comparison
  // in useEditAndReprocess doesn't flag the removal of the default placeholder unit as an edit.
  return flattenedDataToEnrichedCanonical(
    processSpecificationFields,
    documentType,
    flattenedOriginal,
    packingListEnabled,
  );
};
